import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import api from "../api/axios";
import "./RelationGraphPage.responsive.css";

const CATEGORY_CONFIG = [
  {
    id: "concerns",
    type: "category_concern",
    childType: "concern",
    label: "Possible Concerns",
    helper: "Clinical concerns inferred from intake and analysis.",
    field: "possible_concerns",
    x: 230,
    y: -280,
  },
  {
    id: "specialists",
    type: "category_specialist",
    childType: "specialist",
    label: "Specialists",
    helper: "Suggested doctors or departments for review.",
    field: "suggested_specialists",
    x: 230,
    y: -140,
  },
  {
    id: "tests",
    type: "category_test",
    childType: "test",
    label: "Suggested Tests",
    helper: "Investigations or tests suggested by analysis.",
    field: "suggested_tests",
    x: 230,
    y: 0,
  },
  {
    id: "brain-regions",
    type: "category_brain",
    childType: "brain",
    label: "Brain Regions",
    helper: "Brain regions mentioned or inferred by MedGen.",
    field: "brain_regions",
    x: 230,
    y: 140,
  },
  {
    id: "medicine-concerns",
    type: "category_medicine",
    childType: "medicine",
    label: "Medicine Concerns",
    helper: "Medication warnings, interactions, or concern areas.",
    field: "medicine_concerns",
    x: 230,
    y: 280,
  },
];

function normalizeAnalysis(rawAnalysis) {
  if (!rawAnalysis) return null;

  const payload = rawAnalysis.analysis_json || rawAnalysis || {};

  return {
    initial_summary: payload.initial_summary || rawAnalysis.initial_summary || "",
    possible_concerns:
      payload.possible_concerns || rawAnalysis.possible_concerns || [],
    suggested_tests:
      payload.suggested_tests || rawAnalysis.suggested_tests || [],
    suggested_specialists:
      payload.suggested_specialists || rawAnalysis.suggested_specialists || [],
    medicine_concerns:
      payload.medicine_concerns || rawAnalysis.medicine_concerns || [],
    brain_regions:
      payload.brain_regions || rawAnalysis.brain_regions || [],
    doctor_note: payload.doctor_note || rawAnalysis.doctor_note || "",
    triage: payload.triage || rawAnalysis.triage || null,
    raw_text: rawAnalysis.raw_text || payload.raw_text || "",
  };
}

function toText(item) {
  if (!item) return "";
  if (typeof item === "string") return item;
  if (typeof item === "number") return String(item);

  return (
    item.name ||
    item.title ||
    item.region ||
    item.test ||
    item.specialist ||
    item.concern ||
    item.description ||
    item.note ||
    JSON.stringify(item)
  );
}

function shortText(value, limit = 42) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}...`;
}

function formatDate(value) {
  if (!value) return "No date";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "No date";
  }
}

function nodeColor(type) {
  const map = {
    patient: "#1f7bc1",
    session: "#18b7ff",
    analysis: "#07111f",

    category_concern: "#ef4444",
    category_specialist: "#8b5cf6",
    category_test: "#f59e0b",
    category_brain: "#06b6d4",
    category_medicine: "#10b981",
    category_note: "#64748b",

    concern: "#f87171",
    specialist: "#a78bfa",
    test: "#fbbf24",
    brain: "#22d3ee",
    medicine: "#34d399",
    note: "#94a3b8",
    empty: "#94a3b8",
  };

  return map[type] || "#1f7bc1";
}

function nodeLabel(type) {
  const map = {
    patient: "Patient",
    session: "Clinical Session",
    analysis: "MedGen Analysis",

    category_concern: "Concern Cluster",
    category_specialist: "Specialist Cluster",
    category_test: "Test Cluster",
    category_brain: "Brain Region Cluster",
    category_medicine: "Medicine Concern Cluster",
    category_note: "Doctor Note",

    concern: "Possible Concern",
    specialist: "Suggested Specialist",
    test: "Suggested Test",
    brain: "Brain Region",
    medicine: "Medicine Concern",
    note: "Doctor Note",
    empty: "Pending",
  };

  return map[type] || "Node";
}

function getNodeRadius(type) {
  if (type === "patient") return 34;
  if (type === "session") return 31;
  if (type === "analysis") return 38;
  if (String(type).startsWith("category_")) return 34;
  return 22;
}

function getClinicalAction(type) {
  const map = {
    patient: "Review demographic and profile context before interpretation.",
    session: "Open the session timeline and confirm intake completeness.",
    analysis:
      "Use the generated reasoning as a support layer, not a final diagnosis.",
    category_concern:
      "Right-click to view concern subnodes. Prioritize red flags and clinician review.",
    category_specialist:
      "Right-click to view specialist routes connected to this analysis.",
    category_test:
      "Right-click to view investigation subnodes suggested by the analysis.",
    category_brain:
      "Right-click to view brain-region subnodes mentioned by MedGen.",
    category_medicine:
      "Right-click to view medicine-related warnings or concern nodes.",
    category_note:
      "Right-click to view the doctor-facing note generated by analysis.",
    concern: "Validate concern using history, examination, and investigation.",
    specialist: "Consider referral or review by this department.",
    test: "Discuss whether this investigation is clinically appropriate.",
    brain: "Correlate with symptoms and clinical findings.",
    medicine: "Review medication safety and history carefully.",
    note: "Use this as a doctor-facing summary.",
    empty: "Generate or complete more data to improve graph intelligence.",
  };

  return map[type] || "Inspect this node and verify clinically.";
}

function getNodeScore(type) {
  if (type === "analysis") return 92;
  if (String(type).startsWith("category_")) return 78;
  if (["concern", "brain", "medicine"].includes(type)) return 72;
  if (["specialist", "test"].includes(type)) return 68;
  if (type === "patient" || type === "session") return 60;
  return 40;
}

function getCategoryItems(category, analysis) {
  if (!analysis) return [];

  if (category.id === "doctor-note") {
    return analysis.doctor_note ? [analysis.doctor_note] : [];
  }

  return (analysis[category.field] || []).map(toText).filter(Boolean);
}

function isCategoryNode(type) {
  return String(type || "").startsWith("category_");
}

function buildClinicalGraph(patient, session, analysis, expandedCategories) {
  const nodes = [];
  const links = [];

  const expandedSet = new Set(expandedCategories || []);

  const addNode = (node) => {
    nodes.push({
      radius: getNodeRadius(node.type),
      score: getNodeScore(node.type),
      action: getClinicalAction(node.type),
      ...node,
    });
  };

  const addLink = (source, target, label = "", soft = false) => {
    links.push({
      source,
      target,
      label,
      soft,
    });
  };

  addNode({
    id: "patient",
    type: "patient",
    label: patient?.name || "Patient",
    helper: `${patient?.age ?? "—"} yrs · ${patient?.gender || "unknown"} · ${
      patient?.relation || "self"
    }`,
    fx: -560,
    fy: 0,
    isMainNode: true,
  });

  addNode({
    id: "session",
    type: "session",
    label: session?.title || "Clinical Session",
    helper: formatDate(session?.created_at || session?.started_at),
    fx: -360,
    fy: 0,
    isMainNode: true,
  });

  addNode({
    id: "analysis",
    type: "analysis",
    label: analysis ? "MedGen Clinical Analysis" : "Analysis Pending",
    helper: analysis?.initial_summary
      ? shortText(analysis.initial_summary, 180)
      : "Complete questionnaire and generate analysis first.",
    fx: -120,
    fy: 0,
    isMainNode: true,
  });

  addLink("patient", "session", "has session");
  addLink("session", "analysis", "generated");

  if (!analysis) {
    addNode({
      id: "pending-analysis",
      type: "empty",
      label: "No analysis found",
      helper: "Generate MedGen analysis first.",
      fx: 160,
      fy: 0,
      isMainNode: false,
    });

    addLink("analysis", "pending-analysis", "pending", true);
    return { nodes, links };
  }

  const categories = [...CATEGORY_CONFIG];

  if (analysis.doctor_note) {
    categories.push({
      id: "doctor-note",
      type: "category_note",
      childType: "note",
      label: "Doctor Note",
      helper: "Doctor-facing note generated by the analysis.",
      field: "doctor_note",
      x: 230,
      y: 420,
    });
  }

  categories.forEach((category) => {
    const items = getCategoryItems(category, analysis);
    const isExpanded = expandedSet.has(category.id);

    addNode({
      id: category.id,
      type: category.type,
      label: category.label,
      helper: `${category.helper} ${
        items.length
          ? `${items.length} linked item${items.length > 1 ? "s" : ""}.`
          : "No linked items found."
      } Right-click for options.`,
      fx: category.x,
      fy: category.y,
      isMainNode: true,
      categoryId: category.id,
      childType: category.childType,
      childCount: items.length,
      expanded: isExpanded,
    });

    addLink("analysis", category.id, category.label);

    if (!isExpanded) {
      return;
    }

    const visibleItems = items.slice(0, 8);

    if (!visibleItems.length) {
      const emptyId = `${category.id}-empty`;

      addNode({
        id: emptyId,
        type: "empty",
        label: `No ${category.label}`,
        helper: "Not mentioned in current analysis.",
        fx: category.x + 360,
        fy: category.y,
        isMainNode: false,
        parentCategoryId: category.id,
      });

      addLink(category.id, emptyId, "none", true);
      return;
    }

    const spacing = 72;
    const startY = category.y - ((visibleItems.length - 1) * spacing) / 2;

    visibleItems.forEach((item, index) => {
      const childId = `${category.childType}-${category.id}-${index}`;

      addNode({
        id: childId,
        type: category.childType,
        label: shortText(item, 56),
        helper: item,
        raw: item,
        fx: category.x + 390,
        fy: startY + index * spacing,
        isMainNode: false,
        parentCategoryId: category.id,
      });

      addLink(category.id, childId, nodeLabel(category.childType));
    });

    if (items.length > visibleItems.length) {
      const moreId = `${category.id}-more`;

      addNode({
        id: moreId,
        type: "empty",
        label: `+${items.length - visibleItems.length} more`,
        helper: "Additional items hidden to keep graph readable.",
        fx: category.x + 390,
        fy: startY + visibleItems.length * spacing,
        isMainNode: false,
        parentCategoryId: category.id,
      });

      addLink(category.id, moreId, "more", true);
    }
  });

  return { nodes, links };
}

function GraphLegend() {
  const items = [
    ["patient", "Patient"],
    ["analysis", "Analysis"],
    ["category_concern", "Concerns"],
    ["category_specialist", "Specialists"],
    ["category_test", "Tests"],
    ["category_brain", "Brain Regions"],
    ["category_medicine", "Medicine"],
    ["category_note", "Doctor Note"],
  ];

  return (
    <div className="mgclinical-legend-card">
      <h3>Graph Legend</h3>
      <div className="mgclinical-chip-list">
        {items.map(([type, label]) => (
          <span key={type} className="mgclinical-chip">
            <i style={{ background: nodeColor(type) }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ClinicalStats({ analysis, patients, sessions, expandedCategories }) {
  const stats = useMemo(() => {
    return {
      patients: patients.length,
      sessions: sessions.length,
      concerns: analysis?.possible_concerns?.length || 0,
      tests: analysis?.suggested_tests?.length || 0,
      specialists: analysis?.suggested_specialists?.length || 0,
      brain: analysis?.brain_regions?.length || 0,
      expanded: expandedCategories.length,
    };
  }, [analysis, patients, sessions, expandedCategories]);

  return (
    <section className="mgclinical-stats-grid">
      <div className="mgclinical-stat primary">
        <span>Patients</span>
        <strong>{stats.patients}</strong>
        <p>Profiles available for graphing</p>
      </div>

      <div className="mgclinical-stat">
        <span>Sessions</span>
        <strong>{stats.sessions}</strong>
        <p>Sessions loaded for selected patient</p>
      </div>

      <div className="mgclinical-stat">
        <span>Concerns</span>
        <strong>{stats.concerns}</strong>
        <p>Clinical concerns detected</p>
      </div>

      <div className="mgclinical-stat">
        <span>Specialists</span>
        <strong>{stats.specialists}</strong>
        <p>Suggested specialist routes</p>
      </div>

      <div className="mgclinical-stat">
        <span>Tests</span>
        <strong>{stats.tests}</strong>
        <p>Investigation list</p>
      </div>

      <div className="mgclinical-stat">
        <span>Expanded</span>
        <strong>{stats.expanded}</strong>
        <p>Open clinical buckets</p>
      </div>
    </section>
  );
}

function SmartInsightPanel({ node, analysis }) {
  const selected = node || {
    type: "analysis",
    label: "Clinical Intelligence",
    helper:
      analysis?.initial_summary ||
      "Select any node to inspect clinical meaning. Right-click a main category node to view its subnodes.",
    score: analysis ? 85 : 35,
    action: analysis
      ? "Right-click Concerns, Specialists, Tests, Brain Regions, Medicines, or Doctor Note to expand linked subnodes."
      : "Generate analysis first to unlock full graph insights.",
  };

  const color = nodeColor(selected.type);
  const score = selected.score || getNodeScore(selected.type);

  return (
    <div className="mgclinical-smart-panel">
      <div className="mgclinical-smart-top">
        <div>
          <span>Selected Intelligence</span>
          <strong>{selected.label}</strong>
          <p>{nodeLabel(selected.type)}</p>
        </div>

        <div className="mgclinical-score-ring" style={{ "--ring": color }}>
          <b>{score}</b>
          <small>score</small>
        </div>
      </div>

      <div className="mgclinical-insight-box">
        <span>Clinical meaning</span>
        <p>{selected.helper || "No detail available for this node."}</p>
      </div>

      <div className="mgclinical-insight-box">
        <span>Suggested action</span>
        <p>{selected.action || getClinicalAction(selected.type)}</p>
      </div>

      <div className="mgclinical-node-meta-grid">
        <div>
          <span>Node type</span>
          <strong>{nodeLabel(selected.type)}</strong>
        </div>
        <div>
          <span>Interaction</span>
          <strong>
            {selected.isMainNode
              ? "Left-click inspect · Right-click expand"
              : "Left-click inspect · Drag move"}
          </strong>
        </div>
      </div>
    </div>
  );
}

function CategoryDrilldown({ analysis, onSelectVirtual }) {
  const groups = [
    {
      key: "possible_concerns",
      label: "Concerns",
      type: "concern",
      items: analysis?.possible_concerns || [],
    },
    {
      key: "suggested_specialists",
      label: "Specialists",
      type: "specialist",
      items: analysis?.suggested_specialists || [],
    },
    {
      key: "suggested_tests",
      label: "Tests",
      type: "test",
      items: analysis?.suggested_tests || [],
    },
    {
      key: "brain_regions",
      label: "Brain",
      type: "brain",
      items: analysis?.brain_regions || [],
    },
    {
      key: "medicine_concerns",
      label: "Medicines",
      type: "medicine",
      items: analysis?.medicine_concerns || [],
    },
  ];

  return (
    <div className="mgclinical-card">
      <div className="mgclinical-card-head compact">
        <div>
          <span>Analysis Breakdown</span>
          <strong>Clinical buckets</strong>
          <p>
            Graph stays clean by default. Use right-click on graph categories to
            show only the subnodes you need.
          </p>
        </div>
      </div>

      <div className="mgclinical-breakdown">
        {groups.map((group) => (
          <div className="mgclinical-breakdown-group" key={group.key}>
            <div className="mgclinical-breakdown-title">
              <i style={{ background: nodeColor(group.type) }} />
              <strong>{group.label}</strong>
              <span>{group.items.length}</span>
            </div>

            {group.items.length === 0 ? (
              <p className="mgclinical-breakdown-empty">No items found.</p>
            ) : (
              group.items.slice(0, 5).map((item, index) => {
                const text = toText(item);
                return (
                  <button
                    type="button"
                    key={`${group.key}-${index}`}
                    className="mgclinical-breakdown-item"
                    onClick={() =>
                      onSelectVirtual({
                        id: `${group.type}-virtual-${index}`,
                        type: group.type,
                        label: shortText(text, 52),
                        helper: text,
                        score: getNodeScore(group.type),
                        action: getClinicalAction(group.type),
                      })
                    }
                  >
                    {shortText(text, 64)}
                  </button>
                );
              })
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GraphContextMenu({
  menu,
  onClose,
  onExpandOnly,
  onToggle,
  onCollapse,
  onExpandAll,
  onCollapseAll,
}) {
  if (!menu?.node) return null;

  const node = menu.node;
  const canExpandCategory = isCategoryNode(node.type);

  return (
    <div
      className="mgclinical-context-menu"
      style={{
        left: menu.x,
        top: menu.y,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mgclinical-context-head">
        <span>Node Options</span>
        <strong>{shortText(node.label, 36)}</strong>
      </div>

      {canExpandCategory ? (
        <>
          <button type="button" onClick={() => onExpandOnly(node)}>
            View only this node’s subnodes
          </button>

          <button type="button" onClick={() => onToggle(node)}>
            {node.expanded ? "Hide this node’s subnodes" : "Show this node’s subnodes"}
          </button>

          <button type="button" onClick={() => onCollapse(node)}>
            Collapse this node
          </button>
        </>
      ) : (
        <div className="mgclinical-context-note">
          Right-click category nodes like Tests, Specialists, Brain Regions, or
          Concerns to open linked subnodes.
        </div>
      )}

      <button type="button" onClick={onExpandAll}>
        Expand all main nodes
      </button>

      <button type="button" onClick={onCollapseAll}>
        Collapse all subnodes
      </button>

      <button type="button" className="muted" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

export default function RelationGraphPage({ user }) {
  const svgRef = useRef(null);

  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);

  const [analysisData, setAnalysisData] = useState(null);

  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(1);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    const close = () => setContextMenu(null);
    const esc = (e) => {
      if (e.key === "Escape") setContextMenu(null);
    };

    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", esc);

    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", esc);
    };
  }, []);

  useEffect(() => {
    if (selectedPatientId) {
      loadSessions(selectedPatientId);
    } else {
      setSessions([]);
      setSelectedSessionId("");
      setAnalysisData(null);
      setExpandedCategories([]);
    }
  }, [selectedPatientId]);

  useEffect(() => {
    if (selectedSessionId) {
      loadSessionDetail(selectedSessionId);
    } else {
      setAnalysisData(null);
      setExpandedCategories([]);
    }
  }, [selectedSessionId]);

  const loadPatients = async () => {
    try {
      setLoadingPatients(true);

      const res = await api.get("/api/patient/");
      const list = res.data || [];

      setPatients(list);

      if (list.length) {
        setSelectedPatientId(String(list[0].id));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load patients");
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadSessions = async (patientId) => {
    try {
      setLoadingSessions(true);

      const res = await api.get(`/api/session/patient/${patientId}`);
      const list = res.data || [];

      const sorted = [...list].sort(
        (a, b) => Number(b.id || 0) - Number(a.id || 0)
      );

      setSessions(sorted);

      if (sorted.length) {
        setSelectedSessionId(String(sorted[0].id));
      } else {
        setSelectedSessionId("");
        setAnalysisData(null);
        setExpandedCategories([]);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load sessions");
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSessionDetail = async (sessionId) => {
    try {
      setLoadingDetail(true);

      const res = await api.get(`/api/session/detail/${sessionId}`);
      const payload = res.data;

      setAnalysisData(normalizeAnalysis(payload.analysis || null));
      setSelectedNode(null);
      setExpandedCategories([]);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load session detail");
    } finally {
      setLoadingDetail(false);
    }
  };

  const selectedPatient = useMemo(() => {
    return patients.find((p) => String(p.id) === String(selectedPatientId)) || null;
  }, [patients, selectedPatientId]);

  const selectedSession = useMemo(() => {
    return sessions.find((s) => String(s.id) === String(selectedSessionId)) || null;
  }, [sessions, selectedSessionId]);

  const availableCategoryIds = useMemo(() => {
    if (!analysisData) return [];

    const ids = CATEGORY_CONFIG.map((item) => item.id);

    if (analysisData.doctor_note) {
      ids.push("doctor-note");
    }

    return ids;
  }, [analysisData]);

  const graph = useMemo(() => {
    return buildClinicalGraph(
      selectedPatient,
      selectedSession,
      analysisData,
      expandedCategories
    );
  }, [selectedPatient, selectedSession, analysisData, expandedCategories]);

  const expandOnly = (node) => {
    if (!node?.categoryId) return;
    setExpandedCategories([node.categoryId]);
    setSelectedNode(node);
    setContextMenu(null);
  };

  const toggleCategory = (node) => {
    if (!node?.categoryId) return;

    setExpandedCategories((prev) => {
      const exists = prev.includes(node.categoryId);

      if (exists) {
        return prev.filter((id) => id !== node.categoryId);
      }

      return [...prev, node.categoryId];
    });

    setSelectedNode(node);
    setContextMenu(null);
  };

  const collapseCategory = (node) => {
    if (!node?.categoryId) return;

    setExpandedCategories((prev) => prev.filter((id) => id !== node.categoryId));
    setSelectedNode(node);
    setContextMenu(null);
  };

  const expandAllCategories = () => {
    setExpandedCategories(availableCategoryIds);
    setContextMenu(null);
  };

  const collapseAllCategories = () => {
    setExpandedCategories([]);
    setContextMenu(null);
  };

  useEffect(() => {
    if (!svgRef.current || loadingPatients || loadingSessions || loadingDetail) {
      return;
    }

    const svgNode = svgRef.current;
    const svg = d3.select(svgNode);
    svg.selectAll("*").remove();

    const width = svgNode.clientWidth || 1200;
    const height = 920;

    svg.attr("viewBox", `0 0 ${width} ${height}`);

    const defs = svg.append("defs");

    const glow = defs
      .append("filter")
      .attr("id", "mgclinical-glow")
      .attr("x", "-70%")
      .attr("y", "-70%")
      .attr("width", "240%")
      .attr("height", "240%");

    glow
      .append("feGaussianBlur")
      .attr("stdDeviation", "4")
      .attr("result", "coloredBlur");

    const merge = glow.append("feMerge");
    merge.append("feMergeNode").attr("in", "coloredBlur");
    merge.append("feMergeNode").attr("in", "SourceGraphic");

    const container = svg.append("g").attr("class", "mgclinical-graph-layer");

    const zoomBehavior = d3
      .zoom()
      .scaleExtent([0.42, 3])
      .filter((event) => {
        if (event.type === "contextmenu") return false;
        return !event.target.closest?.(".mgclinical-node");
      })
      .on("zoom", (event) => {
        container.attr("transform", event.transform);
        setZoomLevel(event.transform.k);
      });

    svg.call(zoomBehavior);

    const initialTransform = d3.zoomIdentity
      .translate(width / 2 - 90, height / 2 - 20)
      .scale(expandedCategories.length ? 0.76 : 0.9);

    svg.call(zoomBehavior.transform, initialTransform);

    const graphNodes = graph.nodes.map((nodeItem) => ({
      ...nodeItem,
      x: Number(nodeItem.fx || 0),
      y: Number(nodeItem.fy || 0),
    }));

    const graphLinks = graph.links.map((linkItem) => ({ ...linkItem }));
    const nodeById = new Map(graphNodes.map((nodeItem) => [nodeItem.id, nodeItem]));

    function getPoint(id) {
      const item = nodeById.get(id);
      return {
        x: item?.x || 0,
        y: item?.y || 0,
      };
    }

    function curvedPath(source, target) {
      const sx = source.x;
      const sy = source.y;
      const tx = target.x;
      const ty = target.y;
      const midX = (sx + tx) / 2;

      return `M ${sx} ${sy} C ${midX} ${sy}, ${midX} ${ty}, ${tx} ${ty}`;
    }

    const linkGroup = container.append("g").attr("class", "mgclinical-links");

    const links = linkGroup
      .selectAll("path")
      .data(graphLinks)
      .enter()
      .append("path")
      .attr("fill", "none")
      .attr("stroke", (d) =>
        d.soft ? "rgba(31,123,193,0.16)" : "rgba(31,123,193,0.42)"
      )
      .attr("stroke-width", (d) => (d.soft ? 1.3 : 2.5))
      .attr("stroke-dasharray", (d) => (d.soft ? "5 7" : "0"))
      .attr("class", "mgclinical-link-path");

    const linkLabels = container
      .append("g")
      .attr("class", "mgclinical-link-labels")
      .selectAll("text")
      .data(graphLinks.filter((d) => d.label && !d.soft))
      .enter()
      .append("text")
      .attr("font-size", 10)
      .attr("font-weight", 900)
      .attr("fill", "#6b7d94")
      .attr("text-anchor", "middle")
      .attr("paint-order", "stroke")
      .attr("stroke", "rgba(255,255,255,0.82)")
      .attr("stroke-width", 4)
      .attr("stroke-linejoin", "round")
      .text((d) => shortText(d.label, 18));

    const node = container
      .append("g")
      .attr("class", "mgclinical-nodes")
      .selectAll("g")
      .data(graphNodes)
      .enter()
      .append("g")
      .attr("class", (d) => `mgclinical-node ${d.type}`)
      .attr("transform", (d) => `translate(${d.x},${d.y})`)
      .style("cursor", "grab")
      .on("click", (event, d) => {
        event.stopPropagation();
        setSelectedNode({ ...d });
        setContextMenu(null);
      })
      .on("contextmenu", (event, d) => {
        event.preventDefault();
        event.stopPropagation();

        setSelectedNode({ ...d });

        setContextMenu({
          x: Math.min(event.clientX, window.innerWidth - 280),
          y: Math.min(event.clientY, window.innerHeight - 260),
          node: { ...d },
        });
      });

    node
      .append("circle")
      .attr("r", (d) => d.radius + 11)
      .attr("fill", "none")
      .attr("stroke", (d) => nodeColor(d.type))
      .attr("stroke-width", 1.2)
      .attr("opacity", (d) => (d.expanded ? 0.42 : 0.22))
      .attr("class", "mgclinical-node-orbit");

    node
      .append("circle")
      .attr("r", (d) => d.radius)
      .attr("fill", (d) => nodeColor(d.type))
      .attr("stroke", "#ffffff")
      .attr("stroke-width", (d) => (d.expanded ? 5 : 3))
      .attr("filter", "url(#mgclinical-glow)")
      .attr("class", "mgclinical-node-core");

    node
      .append("circle")
      .attr("r", 5)
      .attr("cx", (d) => d.radius * 0.45)
      .attr("cy", (d) => -d.radius * 0.45)
      .attr("fill", "#ffffff")
      .attr("opacity", 0.78);

    node
      .filter((d) => d.isMainNode && isCategoryNode(d.type))
      .append("text")
      .attr("dy", -42)
      .attr("text-anchor", "middle")
      .attr("font-size", 10)
      .attr("font-weight", 950)
      .attr("fill", (d) => (d.expanded ? "#0b4e84" : "#6b7d94"))
      .attr("paint-order", "stroke")
      .attr("stroke", "rgba(255,255,255,0.9)")
      .attr("stroke-width", 4)
      .text((d) =>
        d.expanded
          ? `${d.childCount || 0} subnodes open`
          : `Right-click · ${d.childCount || 0}`
      );

    node
      .append("text")
      .attr("dy", (d) => d.radius + 23)
      .attr("text-anchor", "middle")
      .attr("font-size", (d) =>
        String(d.type).startsWith("category_") ? 13 : 12
      )
      .attr("font-weight", 950)
      .attr("fill", "#07111f")
      .attr("paint-order", "stroke")
      .attr("stroke", "rgba(255,255,255,0.86)")
      .attr("stroke-width", 5)
      .attr("stroke-linejoin", "round")
      .text((d) => shortText(d.label, 31));

    node
      .append("text")
      .attr("dy", (d) => d.radius + 41)
      .attr("text-anchor", "middle")
      .attr("font-size", 10)
      .attr("font-weight", 800)
      .attr("fill", "#66778e")
      .attr("paint-order", "stroke")
      .attr("stroke", "rgba(255,255,255,0.86)")
      .attr("stroke-width", 4)
      .attr("stroke-linejoin", "round")
      .text((d) => nodeLabel(d.type));

    function updateGraphPositions() {
      links.attr("d", (d) => {
        const source = getPoint(d.source);
        const target = getPoint(d.target);
        return curvedPath(source, target);
      });

      linkLabels
        .attr("x", (d) => {
          const source = getPoint(d.source);
          const target = getPoint(d.target);
          return (source.x + target.x) / 2;
        })
        .attr("y", (d) => {
          const source = getPoint(d.source);
          const target = getPoint(d.target);
          return (source.y + target.y) / 2 - 8;
        });

      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
    }

    const dragBehavior = d3
      .drag()
      .on("start", function () {
        d3.select(this).style("cursor", "grabbing");
      })
      .on("drag", function (event, d) {
        d.x = event.x;
        d.y = event.y;
        d.fx = event.x;
        d.fy = event.y;
        updateGraphPositions();
      })
      .on("end", function (_, d) {
        d3.select(this).style("cursor", "grab");
        setSelectedNode({ ...d });
      });

    node.call(dragBehavior);

    updateGraphPositions();

    return () => {
      svg.on(".zoom", null);
    };
  }, [
    graph,
    loadingPatients,
    loadingSessions,
    loadingDetail,
    expandedCategories,
  ]);

  return (
    <div className="mgclinical-page">
      <style>{clinicalGraphCss}</style>

      <GraphContextMenu
        menu={contextMenu}
        onClose={() => setContextMenu(null)}
        onExpandOnly={expandOnly}
        onToggle={toggleCategory}
        onCollapse={collapseCategory}
        onExpandAll={expandAllCategories}
        onCollapseAll={collapseAllCategories}
      />

      <section className="mgclinical-hero">
        <div className="mgclinical-hero-copy">
          <div className="mgclinical-kicker">Clinical Intelligence Graph</div>
          <h1>Patient Analysis Map</h1>
          <p>
            Visualize MedGen analysis as a structured clinical intelligence map.
            Main nodes stay visible first. Right-click any category node to view
            only its connected subnodes.
          </p>

          <div className="mgclinical-controls">
            <div className="mgclinical-field">
              <label>Patient</label>
              <select
                value={selectedPatientId}
                onChange={(e) => setSelectedPatientId(e.target.value)}
                disabled={loadingPatients}
              >
                {!patients.length && <option value="">No patients</option>}
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name} · {patient.relation || "self"} ·{" "}
                    {patient.age ?? "—"} yrs
                  </option>
                ))}
              </select>
            </div>

            <div className="mgclinical-field">
              <label>Session</label>
              <select
                value={selectedSessionId}
                onChange={(e) => setSelectedSessionId(e.target.value)}
                disabled={!selectedPatientId || loadingSessions}
              >
                {!sessions.length && <option value="">No sessions</option>}
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.title || `Session #${session.id}`} ·{" "}
                    {formatDate(session.created_at)}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="mgclinical-btn primary"
              onClick={() => {
                if (selectedSessionId) loadSessionDetail(selectedSessionId);
              }}
              disabled={!selectedSessionId || loadingDetail}
            >
              {loadingDetail ? "Loading..." : "Reload Graph"}
            </button>

            <button
              type="button"
              className="mgclinical-btn soft"
              onClick={collapseAllCategories}
              disabled={!expandedCategories.length}
            >
              Collapse All
            </button>
          </div>
        </div>

        <div className="mgclinical-hero-panel">
          <div className="mgclinical-panel-top">
            <div>
              <span>Graph Status</span>
              <strong>
                {analysisData ? "Structured analysis loaded" : "Waiting for analysis"}
              </strong>
            </div>
            <em>{zoomLevel.toFixed(1)}x</em>
          </div>

          <p>
            Default view shows main clinical nodes only. Right-click a main node
            like Tests, Specialists, or Brain Regions to open only that branch.
          </p>

          <div className="mgclinical-ai-radar">
            <i />
            <span />
            <b />
            <div className="mgclinical-radar-dot d1" />
            <div className="mgclinical-radar-dot d2" />
            <div className="mgclinical-radar-dot d3" />
          </div>
        </div>
      </section>

      <ClinicalStats
        analysis={analysisData}
        patients={patients}
        sessions={sessions}
        expandedCategories={expandedCategories}
      />

      <section className="mgclinical-layout">
        <div className="mgclinical-main-column">
          <div className="mgclinical-card mgclinical-canvas-card">
            <div className="mgclinical-card-head">
              <div>
                <span>Clinical Map</span>
                <strong>Analysis visualization</strong>
                <p>
                  Left-click nodes to inspect. Right-click category nodes to show
                  or hide their linked subnodes. Drag nodes to rearrange.
                </p>
              </div>

              <div className="mgclinical-meta">
                <span>{graph.nodes.length} nodes</span>
                <span>{graph.links.length} links</span>
                <span>{expandedCategories.length} expanded</span>
                <span>Right-click enabled</span>
              </div>
            </div>

            {loadingPatients || loadingSessions || loadingDetail ? (
              <div className="mgclinical-empty">Loading clinical graph...</div>
            ) : !selectedPatient ? (
              <div className="mgclinical-empty">Select a patient to build graph.</div>
            ) : !selectedSession ? (
              <div className="mgclinical-empty">
                No session found for selected patient.
              </div>
            ) : (
              <div className="mgclinical-svg-wrap">
                <svg ref={svgRef} className="mgclinical-svg" />
              </div>
            )}
          </div>

          <CategoryDrilldown
            analysis={analysisData}
            onSelectVirtual={(node) => setSelectedNode(node)}
          />
        </div>

        <aside className="mgclinical-side">
          <SmartInsightPanel node={selectedNode} analysis={analysisData} />

          <div className="mgclinical-card">
            <div className="mgclinical-card-head compact">
              <div>
                <span>Patient Context</span>
                <strong>{selectedPatient?.name || "No patient"}</strong>
                <p>
                  {selectedPatient
                    ? `${selectedPatient.age ?? "—"} yrs · ${
                        selectedPatient.gender || "unknown"
                      } · ${selectedPatient.relation || "self"}`
                    : "Select patient first"}
                </p>
              </div>
            </div>

            <div className="mgclinical-detail-stack">
              <div>
                <span>Session</span>
                <strong>
                  {selectedSession?.title ||
                    (selectedSession ? `Session #${selectedSession.id}` : "—")}
                </strong>
              </div>

              <div>
                <span>Created</span>
                <strong>{formatDate(selectedSession?.created_at)}</strong>
              </div>

              <div>
                <span>Analysis</span>
                <strong>{analysisData ? "Available" : "Pending"}</strong>
              </div>

              <div>
                <span>Open Branches</span>
                <strong>{expandedCategories.length}</strong>
              </div>
            </div>
          </div>

          <div className="mgclinical-card">
            <GraphLegend />
          </div>
        </aside>
      </section>
    </div>
  );
}

const clinicalGraphCss = `
.mgclinical-page {
  --mg-black: #03060c;
  --mg-navy: #06101f;
  --mg-navy-2: #081525;
  --mg-blue: #1f7bc1;
  --mg-cyan: #18b7ff;
  --mg-text: #07111f;
  --mg-muted: #66778e;
  --mg-border: #cddcec;
  --mg-soft: #f6faff;

  width: 100%;
  min-height: calc(100vh - 68px);
  padding: 28px 34px 46px;
  color: var(--mg-text);
}

.mgclinical-page * {
  box-sizing: border-box;
}

.mgclinical-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  gap: 26px;
  align-items: stretch;
  margin-bottom: 22px;
  padding: 30px;
  border-radius: 28px;
  background:
    radial-gradient(circle at 8% 0%, rgba(24,183,255,0.10), transparent 32%),
    linear-gradient(180deg, rgba(255,255,255,0.98), rgba(247,251,255,0.94));
  border: 1px solid var(--mg-border);
  box-shadow: 0 24px 70px rgba(7, 17, 31, 0.08);
}

.mgclinical-kicker {
  margin-bottom: 12px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.mgclinical-hero h1 {
  margin: 0;
  color: var(--mg-text);
  font-size: clamp(40px, 4.1vw, 62px);
  line-height: 1.02;
  letter-spacing: -0.055em;
  font-weight: 950;
}

.mgclinical-hero p {
  max-width: 850px;
  margin: 18px 0 20px;
  color: var(--mg-muted);
  font-size: 15px;
  line-height: 1.7;
}

.mgclinical-controls {
  display: grid;
  grid-template-columns: minmax(190px, 1fr) minmax(250px, 1.2fr) auto auto;
  gap: 12px;
  align-items: end;
}

.mgclinical-field {
  display: grid;
  gap: 7px;
}

.mgclinical-field label {
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgclinical-field select,
.mgclinical-field input {
  width: 100%;
  min-height: 44px;
  padding: 10px 13px;
  border-radius: 13px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.96);
  color: #07111f;
  font-size: 13px;
  font-weight: 800;
  outline: none;
}

.mgclinical-field select:focus {
  border-color: rgba(31,123,193,0.72);
  box-shadow: 0 0 0 4px rgba(24,183,255,0.12);
}

.mgclinical-btn {
  min-height: 44px;
  padding: 10px 16px;
  border-radius: 13px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.16s ease;
  white-space: nowrap;
}

.mgclinical-btn.primary {
  color: white;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 14px 28px rgba(31, 123, 193, 0.24);
}

.mgclinical-btn.soft {
  color: #112238;
  background: #f8fbff;
  border-color: var(--mg-border);
}

.mgclinical-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.mgclinical-hero-panel {
  position: relative;
  overflow: hidden;
  padding: 22px;
  border-radius: 24px;
  color: white;
  background:
    radial-gradient(circle at 92% 0%, rgba(24, 183, 255, 0.26), transparent 36%),
    radial-gradient(circle at 0% 100%, rgba(31, 123, 193, 0.18), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 24px 70px rgba(3, 6, 12, 0.18);
}

.mgclinical-panel-top,
.mgclinical-card-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
}

.mgclinical-panel-top span,
.mgclinical-card-head span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.mgclinical-card-head span {
  color: #125a94;
}

.mgclinical-panel-top strong {
  color: white;
  font-size: 22px;
  letter-spacing: -0.04em;
}

.mgclinical-card-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 24px;
  letter-spacing: -0.045em;
  font-weight: 950;
}

.mgclinical-panel-top em {
  padding: 5px 10px;
  border-radius: 999px;
  font-style: normal;
  color: #bfeaff;
  background: rgba(24,183,255,0.12);
  border: 1px solid rgba(24,183,255,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgclinical-hero-panel p {
  color: #a9bdd3;
}

.mgclinical-ai-radar {
  position: relative;
  height: 190px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 50%, rgba(24,183,255,0.18), transparent 46%),
    rgba(255,255,255,0.055);
  border: 1px solid rgba(24,183,255,0.13);
}

.mgclinical-ai-radar::before {
  content: "";
  position: absolute;
  width: 180px;
  height: 2px;
  transform-origin: left center;
  left: 50%;
  top: 50%;
  background: linear-gradient(90deg, rgba(24,183,255,0.70), transparent);
  animation: mgclinicalRadarSweep 3s linear infinite;
}

.mgclinical-ai-radar i,
.mgclinical-ai-radar span,
.mgclinical-ai-radar b {
  position: absolute;
  border-radius: 999px;
  border: 1px solid rgba(24,183,255,0.34);
  animation: mgclinicalPulse 2.8s ease-in-out infinite;
}

.mgclinical-ai-radar i {
  width: 78px;
  height: 78px;
}

.mgclinical-ai-radar span {
  width: 124px;
  height: 124px;
  animation-delay: 0.25s;
}

.mgclinical-ai-radar b {
  width: 170px;
  height: 170px;
  animation-delay: 0.5s;
}

.mgclinical-radar-dot {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #18b7ff;
  box-shadow: 0 0 18px rgba(24,183,255,0.9);
}

.mgclinical-radar-dot.d1 {
  left: 35%;
  top: 38%;
}

.mgclinical-radar-dot.d2 {
  left: 62%;
  top: 55%;
}

.mgclinical-radar-dot.d3 {
  left: 47%;
  top: 68%;
}

.mgclinical-stats-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.mgclinical-stat {
  min-height: 126px;
  padding: 18px;
  border-radius: 20px;
  border: 1px solid var(--mg-border);
  background: rgba(255,255,255,0.94);
  box-shadow: 0 14px 40px rgba(7, 17, 31, 0.058);
}

.mgclinical-stat.primary {
  color: white;
  background:
    radial-gradient(circle at 90% 0%, rgba(24,183,255,0.28), transparent 34%),
    linear-gradient(135deg, #1f7bc1, #125a94);
  border-color: transparent;
}

.mgclinical-stat span {
  display: block;
  margin-bottom: 10px;
  color: #75869d;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.mgclinical-stat.primary span {
  color: rgba(255,255,255,0.75);
}

.mgclinical-stat strong {
  display: block;
  color: var(--mg-text);
  font-size: 29px;
  font-weight: 950;
}

.mgclinical-stat.primary strong {
  color: white;
}

.mgclinical-stat p {
  margin: 8px 0 0;
  color: var(--mg-muted);
  line-height: 1.45;
  font-size: 12px;
}

.mgclinical-stat.primary p {
  color: rgba(255,255,255,0.86);
}

.mgclinical-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 370px;
  gap: 18px;
  align-items: start;
}

.mgclinical-main-column {
  display: grid;
  gap: 18px;
  min-width: 0;
}

.mgclinical-card {
  padding: 22px;
  border-radius: 24px;
  border: 1px solid var(--mg-border);
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.055), transparent 32%),
    rgba(255,255,255,0.96);
  box-shadow: 0 16px 44px rgba(7,17,31,0.065);
}

.mgclinical-card-head {
  margin-bottom: 16px;
}

.mgclinical-card-head.compact {
  margin-bottom: 12px;
}

.mgclinical-card-head p {
  margin: 7px 0 0;
  color: var(--mg-muted);
  line-height: 1.5;
}

.mgclinical-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.mgclinical-meta span {
  padding: 7px 10px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31,123,193,0.10);
  border: 1px solid rgba(31,123,193,0.18);
  font-size: 11px;
  font-weight: 900;
}

.mgclinical-svg-wrap {
  position: relative;
  overflow: hidden;
  height: 920px;
  border-radius: 24px;
  background:
    radial-gradient(circle at 52% 38%, rgba(24,183,255,0.15), transparent 39%),
    radial-gradient(circle at 10% 15%, rgba(31,123,193,0.12), transparent 28%),
    linear-gradient(180deg, #f8fbff, #eaf5ff);
  border: 1px solid #d9e8f6;
}

.mgclinical-svg-wrap::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(31,123,193,0.065) 1px, transparent 1px),
    linear-gradient(90deg, rgba(31,123,193,0.065) 1px, transparent 1px);
  background-size: 34px 34px;
  pointer-events: none;
}

.mgclinical-svg-wrap::after {
  content: "";
  position: absolute;
  inset: 18px;
  border-radius: 20px;
  border: 1px solid rgba(31,123,193,0.10);
  pointer-events: none;
}

.mgclinical-svg {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
}

.mgclinical-node {
  user-select: none;
}

.mgclinical-node text {
  pointer-events: none;
}

.mgclinical-node:active {
  cursor: grabbing;
}

.mgclinical-node-orbit {
  animation: mgclinicalNodePulse 2.3s ease-in-out infinite;
}

.mgclinical-node-core {
  transition: 0.18s ease;
}

.mgclinical-node:hover .mgclinical-node-core {
  stroke-width: 5;
}

.mgclinical-link-path {
  filter: drop-shadow(0 0 4px rgba(24,183,255,0.18));
}

.mgclinical-side {
  display: grid;
  gap: 18px;
  position: sticky;
  top: 88px;
}

.mgclinical-smart-panel {
  padding: 22px;
  border-radius: 24px;
  color: white;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.22), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 62%, #06101f);
  border: 1px solid rgba(24,183,255,0.18);
  box-shadow: 0 24px 60px rgba(3,6,12,0.16);
}

.mgclinical-smart-top {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.mgclinical-smart-top span,
.mgclinical-insight-box span,
.mgclinical-node-meta-grid span {
  display: block;
  margin-bottom: 8px;
  color: #9fdcff;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.mgclinical-smart-top strong {
  display: block;
  color: white;
  font-size: 24px;
  line-height: 1.12;
  letter-spacing: -0.045em;
}

.mgclinical-smart-top p {
  margin: 7px 0 0;
  color: #a9bdd3;
  font-weight: 800;
}

.mgclinical-score-ring {
  width: 78px;
  height: 78px;
  border-radius: 999px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  align-content: center;
  background:
    radial-gradient(circle at center, #081525 58%, transparent 60%),
    conic-gradient(var(--ring) 0 72%, rgba(255,255,255,0.14) 72% 100%);
  border: 1px solid rgba(255,255,255,0.10);
}

.mgclinical-score-ring b {
  color: white;
  font-size: 20px;
  font-weight: 950;
  line-height: 1;
}

.mgclinical-score-ring small {
  color: #a9bdd3;
  font-size: 10px;
  font-weight: 850;
}

.mgclinical-insight-box {
  padding: 15px;
  border-radius: 17px;
  background: rgba(255,255,255,0.075);
  border: 1px solid rgba(24,183,255,0.13);
  margin-top: 12px;
}

.mgclinical-insight-box p {
  margin: 0;
  color: #dbeafe;
  line-height: 1.55;
}

.mgclinical-node-meta-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-top: 12px;
}

.mgclinical-node-meta-grid > div {
  padding: 14px;
  border-radius: 16px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(24,183,255,0.11);
}

.mgclinical-node-meta-grid strong {
  color: white;
  font-size: 13px;
  font-weight: 950;
}

.mgclinical-detail-stack {
  display: grid;
  gap: 12px;
}

.mgclinical-detail-stack > div {
  padding: 15px;
  border-radius: 16px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.mgclinical-detail-stack span {
  display: block;
  margin-bottom: 7px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgclinical-detail-stack strong {
  color: var(--mg-text);
  font-size: 15px;
  font-weight: 950;
  overflow-wrap: anywhere;
}

.mgclinical-empty {
  padding: 24px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
  color: var(--mg-muted);
  font-weight: 800;
}

.mgclinical-breakdown {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.mgclinical-breakdown-group {
  min-width: 0;
  padding: 14px;
  border-radius: 18px;
  background: #f7fbff;
  border: 1px solid var(--mg-border);
}

.mgclinical-breakdown-title {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 10px;
}

.mgclinical-breakdown-title i {
  width: 11px;
  height: 11px;
  border-radius: 999px;
  box-shadow: 0 0 18px currentColor;
}

.mgclinical-breakdown-title strong {
  color: var(--mg-text);
  font-size: 13px;
  font-weight: 950;
  flex: 1;
}

.mgclinical-breakdown-title span {
  width: 25px;
  height: 25px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  color: #0b4e84;
  background: rgba(31,123,193,0.10);
  border: 1px solid rgba(31,123,193,0.18);
  font-size: 11px;
  font-weight: 950;
}

.mgclinical-breakdown-item {
  width: 100%;
  display: block;
  text-align: left;
  margin-top: 8px;
  padding: 9px 10px;
  border-radius: 12px;
  border: 1px solid #d9e8f6;
  color: #07111f;
  background: rgba(255,255,255,0.95);
  font-size: 12px;
  font-weight: 850;
  cursor: pointer;
}

.mgclinical-breakdown-item:hover {
  border-color: rgba(31,123,193,0.55);
  background: #eef6ff;
}

.mgclinical-breakdown-empty {
  margin: 0;
  color: var(--mg-muted);
  font-size: 12px;
}

.mgclinical-legend-card h3 {
  margin: 0 0 14px;
  color: var(--mg-text);
  font-size: 20px;
  letter-spacing: -0.035em;
}

.mgclinical-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
}

.mgclinical-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 30px;
  padding: 7px 10px;
  border-radius: 999px;
  color: #0b4e84;
  background: rgba(31,123,193,0.08);
  border: 1px solid rgba(31,123,193,0.14);
  font-size: 12px;
  font-weight: 850;
}

.mgclinical-chip i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.mgclinical-context-menu {
  position: fixed;
  z-index: 9999;
  width: 270px;
  padding: 10px;
  border-radius: 16px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24,183,255,0.13), transparent 34%),
    #ffffff;
  border: 1px solid var(--mg-border);
  box-shadow:
    0 22px 60px rgba(7,17,31,0.18),
    0 0 0 1px rgba(24,183,255,0.08);
}

.mgclinical-context-head {
  padding: 10px 10px 12px;
  border-bottom: 1px solid #d9e8f6;
  margin-bottom: 8px;
}

.mgclinical-context-head span {
  display: block;
  margin-bottom: 5px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.mgclinical-context-head strong {
  display: block;
  color: var(--mg-text);
  font-size: 15px;
  font-weight: 950;
  line-height: 1.25;
}

.mgclinical-context-menu button {
  width: 100%;
  min-height: 38px;
  margin-top: 6px;
  padding: 9px 10px;
  border-radius: 12px;
  border: 1px solid transparent;
  color: #07111f;
  background: #f7fbff;
  font-size: 12px;
  font-weight: 900;
  text-align: left;
  cursor: pointer;
}

.mgclinical-context-menu button:hover {
  border-color: rgba(31,123,193,0.35);
  background: #eef6ff;
}

.mgclinical-context-menu button.muted {
  color: #66778e;
  background: white;
  border-color: #d9e8f6;
}

.mgclinical-context-note {
  padding: 10px;
  border-radius: 12px;
  color: #66778e;
  background: #f7fbff;
  border: 1px solid #d9e8f6;
  font-size: 12px;
  line-height: 1.45;
}

@keyframes mgclinicalPulse {
  0% {
    opacity: 0.25;
    transform: scale(0.9);
  }

  50% {
    opacity: 0.75;
    transform: scale(1.04);
  }

  100% {
    opacity: 0.25;
    transform: scale(0.9);
  }
}

@keyframes mgclinicalNodePulse {
  0%, 100% {
    opacity: 0.15;
    transform: scale(0.95);
  }

  50% {
    opacity: 0.42;
    transform: scale(1.08);
  }
}

@keyframes mgclinicalRadarSweep {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1320px) {
  .mgclinical-hero,
  .mgclinical-layout {
    grid-template-columns: 1fr;
  }

  .mgclinical-side {
    position: static;
  }

  .mgclinical-hero-panel {
    max-width: 620px;
  }

  .mgclinical-stats-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .mgclinical-breakdown {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 980px) {
  .mgclinical-page {
    padding: 20px;
  }

  .mgclinical-controls {
    grid-template-columns: 1fr;
  }

  .mgclinical-svg-wrap {
    height: 760px;
  }
}

@media (max-width: 760px) {
  .mgclinical-page {
    padding: 14px;
  }

  .mgclinical-hero {
    padding: 20px;
  }

  .mgclinical-hero h1 {
    font-size: 36px;
  }

  .mgclinical-stats-grid,
  .mgclinical-breakdown {
    grid-template-columns: 1fr;
  }

  .mgclinical-btn {
    width: 100%;
  }

  .mgclinical-card {
    padding: 18px;
  }

  .mgclinical-svg-wrap {
    height: 680px;
  }

  .mgclinical-panel-top,
  .mgclinical-card-head {
    flex-direction: column;
  }

  .mgclinical-context-menu {
    width: calc(100vw - 28px);
    left: 14px !important;
    right: 14px;
  }
}
`;